import { formPages, layoutOf, blockComponent } from '@ayumia/component-runtime';
import { useState, type ReactNode } from 'react';
import type { ApiClient } from '../../api/client';
import { text } from '../../app/i18n';
import { Icon } from '../../design-system/icons';
import { sectionRegion } from '../../work/FormLayoutParts';
import type { InspectorView } from '../editor/canvas-types';
import { setAt } from '../editor/json-pointer';
import { PropertyPanel } from '../editor/PropertyPanel';
import type { SchemaRegistry } from '../editor/schema-properties';
import { addBlankPage } from './canvas/drop-targets';
import { type ConformityRuleOption } from './forms-editors';
import { templateProperties } from './forms-config';
import { answerScope, Note, pickProperties, Refusal, TextComposer, valueSources } from './inspector-controls';
import { aggregatesOf, blocksOf, declaredPages, moveSectionToPage, movePage, movePageTo, removeAggregate, renameId, setBlockBinding, setBlockProp, setLayoutField, setNavigation, setPlacement, updateAggregate, updateBlock, updatePage, addAggregate, type Aggregate, type Template } from './layout-model';
import { blockOf, componentByName, currentLayout, flutterSupported, fr, refusalOf, type EditorRegistry } from './layout-registry';
import { AddButton, Choice, Line, Rule, Sub, Switch } from './v2/controls';
import { copyName, DeletePageDialog, NameDialog } from './v2/dialogs';
import { fieldViewV2 } from './v2/field-inspector';
import { EMPTY_CATALOG, layerLabel, type StudioCatalog } from './v2/library';
import { clearSuggestions, keepSuggestionMarks, LanguesButton, LocaleProvider, LocalizedText, scopeTexts, templateLocales, type LanguesPort, type LanguesScope, type LocaleEnv, type PendingSuggestion, type TranslatePort } from './v2/locales';
import { DISPLAY_BLOCK_PROPS, DISPLAY_BLOCKS, DisplayBlockSettings } from './v2/block-settings';
import { MediaPicker, OPTION_ICONS } from './v2/MediaPicker';
import { deletePageV2, duplicatePage, pageFieldLabels, primaryConflict, setFooter, setHeader, setShortcut, setVisual, submitUnreachable } from './v2/pages';
import { PRESET_SAVE_UNAVAILABLE, type PresetsPort } from './v2/PresetDialogs';
import { FormMoments, ReactionEditor } from './v2/reactions';

/**
 * Inspecteur du profil « formulaire », éditeur v2 (ADR-146 addendum (14), (16), (18) ; planches « EditeurV2-* ») :
 * trois onglets toujours visibles — Contenu, Contrôles, Réactions — pour chaque élément (formulaire, bandeau du
 * haut, pages, page, partie, champ, bloc, barre du bas). Les réglages vivent dans des sections repliables à
 * contrôles visuels ; une explication passe par l'infobulle ⓘ. Aucun identifiant, chemin ni JSON : les noms
 * internes ne paraissent qu'en mode développeur.
 */

export type InspectorFactory = (template: Template, id: string, apply: (next: Template, coalesce?: string) => void, select?: (id: string) => void) => InspectorView;
type Apply = (next: Template, coalesce?: string) => void;
type Localized = Record<string, string>;

const EMPHASIS = [{ value: 'none', label: 'Neutre' }, { value: 'accent', label: 'Accent' }, { value: 'attention', label: 'Attention' }] as const;
const SIZES: Readonly<Record<string, string>> = { compact: 'Compacte', normal: 'Normale', large: 'Grande' };
const VARIANTS: Readonly<Record<string, string>> = { large: 'Large', compact: 'Compact', line: 'Ligne', column: 'Colonne', band: 'Bande', contain: 'Image entière', cover: 'Remplie', banner: 'Bannière', secondary: 'Secondaire', primary: 'Principal', link: 'Lien', neutral: 'Neutre', accent: 'Accent' };
const OPS: Readonly<Record<Aggregate['op'], string>> = { count: 'Nombre de lignes', sum: 'Somme de', min: 'Minimum de', max: 'Maximum de', avg: 'Moyenne de', count_distinct: 'Valeurs différentes de', duplicates: 'Doublons de' };
const INTENTS = [{ value: 'series_entry', label: 'Saisie longue' }, { value: 'short_check', label: 'Contrôle court' }, { value: 'ordered_procedure', label: 'Procédure' }, { value: 'record_view', label: 'Consultation' }];
const SUCCESS_ACTIONS: ReadonlyArray<[string, string]> = [['open_document', 'Ouvrir le document'], ['print_document', 'Imprimer le document'], ['next_work_item', 'Tâche suivante'], ['new_same', 'Recommencer'], ['back_to_inbox', 'Revenir aux tâches']];
export const NAVIGATION_LABELS: Readonly<Record<string, string>> = { single: 'Une page', tabs: 'Onglets', wizard: 'Étapes', steps_with_summary: 'Étapes + synthèse' };
const FOOTER = [{ value: 'previous', label: 'Précédent' }, { value: 'next', label: 'Suivant, puis Valider' }, { value: 'submit', label: 'Valider' }, { value: 'progress', label: 'Progression' }, { value: 'save_status', label: 'Enregistrement' }, { value: 'none', label: 'Rien' }];
const BACKGROUNDS = [{ value: 'none', label: 'Aucun' }, { value: 'neutral', label: 'Neutre' }, { value: 'accent', label: 'Accent' }, { value: 'success', label: 'Vert' }, { value: 'warning', label: 'Orange' }, { value: 'danger', label: 'Rouge' }, { value: 'info', label: 'Bleu' }];

export interface InspectorOptions {
  registry: EditorRegistry;
  schemas: SchemaRegistry;
  rules: readonly ConformityRuleOption[];
  /** Mode développeur (noms internes), réglé dans le menu ⋯ de la barre de l'éditeur. */
  /** Mode développeur (noms internes), réglé dans le menu ⋯ de la barre de l'éditeur (addendum (15)). */
  developer: boolean;
  /** Ouvre le choix du modèle de page et de la navigation sur ce brouillon. */
  onChooseLayout: (template: Template, apply: Apply) => void;
  /** Bibliothèque du tenant : modèle, préréglages, noms des couches (éditeur v2). */
  catalog?: StudioCatalog;
  presets?: PresetsPort;
  /** Client de l'API : médiathèque (sélecteur d'images). */
  client?: ApiClient;
  /** Langue par défaut du tenant et langues ajoutées dans la session. */
  defaultLocale?: string;
  addedLocales?: readonly string[];
  onAddLocale?: (locale: string) => void;
  /** Traduction suggérée par la capacité d'IA du Core, si elle est servie dans ce tenant. */
  translate?: TranslatePort;
}

const NO_PRESETS: PresetsPort = { usages: () => Promise.reject(new Error('Usages indisponibles.')), saveUnavailable: PRESET_SAVE_UNAVAILABLE };

function InternalName({ value, onRename }: { value: string; onRename?: (next: string) => void }) {
  const [draft, setDraft] = useState(value);
  return (
    <Line label="Nom interne">
      <input type="text" aria-label="Nom interne" value={draft} readOnly={onRename === undefined} onChange={(event) => setDraft(event.target.value)} onBlur={() => onRename?.(draft)} />
      {onRename !== undefined && !/^[a-z][a-z0-9_]*$/.test(draft) && <span className="ay-field__error">Minuscules, chiffres et « _ ».</span>}
    </Line>
  );
}

/** Noms internes, en mode développeur seulement (réglé dans le menu ⋯ de la barre, addendum (15)) ; sinon rien. */
function DeveloperFold({ options, children }: { options: InspectorOptions; children?: ReactNode }) {
  if (!options.developer || children === undefined) return null;
  return (
    <>{children}</>
  );
}

/** Un réglage refusé par la recette ou les zones : le motif s'affiche et rien ne change. */
function useChecked(registry: EditorRegistry, template: Template, apply: Apply): [string | null, (next: Template) => void] {
  const [refusal, setRefusal] = useState<string | null>(null);
  return [refusal, (next) => {
    const refused = refusalOf(registry, template, next);
    setRefusal(refused);
    if (refused === null) apply(next);
  }];
}

/** Résumé en un mot des contrôles d'un élément : sa visibilité. */
const visibleWord = (condition: unknown): string => (condition === undefined ? 'Toujours' : condition === false ? 'Jamais' : 'Conditionnel');

// ---- Formulaire.

function FormIdentity({ options, template, apply }: { options: InspectorOptions; template: Template; apply: Apply }) {
  const [picking, setPicking] = useState(false);
  const visual = template.visual as { image?: { file_id?: string }; icon?: string; background?: string } | undefined;
  const kind = visual?.image !== undefined ? 'image' : visual?.icon !== undefined ? 'icon' : 'none';
  const header = (layoutOf(template)?.header ?? {}) as { overline?: Localized };
  return (
    <Sub>
      <LocalizedText label="Nom" required pointer="/label" value={template.label as Localized} onChange={(value) => apply(setAt(template, '/label', value ?? { fr: '' }), 'form.label')} />
      <LocalizedText label="Surtitre" pointer="/layout/header/overline" value={header.overline} onChange={(value) => apply(setHeader(template, { overline: value }), 'form.overline')} />
      <LocalizedText label="Description" multiline pointer="/description" value={template.description as Localized | undefined} onChange={(value) => apply(setAt(template, '/description', value), 'form.description')} />
      <Line label="Image ou icône">
        <span className="ay-v2-inline">
          {/* La médiathèque s'ouvre depuis la vignette elle-même. */}
          <button type="button" className="ay-v2-visual ay-v2-visual-button" aria-label="Changer l’image ou l’icône" disabled={options.client === undefined} title={options.client === undefined ? 'Médiathèque indisponible ici' : 'Changer l’image ou l’icône'} onClick={() => setPicking(true)}>{kind === 'icon' ? <Icon name={visual!.icon} size={18} /> : kind === 'image' ? <Icon name="image" size={18} /> : <Icon name="clipboard" size={18} />}</button>
          {kind !== 'none' && <button type="button" className="ay-v2-link" onClick={() => apply(setVisual(template, visual?.background === undefined ? undefined : ({ background: visual.background } as never)))}>Retirer</button>}
        </span>
      </Line>
      {kind !== 'none' && <Choice label="Fond du visuel" value={visual?.background ?? 'none'} options={BACKGROUNDS} onChange={(value) => apply(setVisual(template, { ...(visual ?? {}), background: value === 'none' ? undefined : value } as never))} />}
      {picking && options.client !== undefined && (
        <MediaPicker
          client={options.client}
          title="Image du formulaire"
          subtitle="Montrée dans le bandeau du haut"
          onClose={() => setPicking(false)}
          onChoose={(choice) => {
            setPicking(false);
            const base = { ...(visual?.background === undefined ? {} : { background: visual.background }) };
            apply(setVisual(template, ('image' in choice ? { ...base, image: choice.image } : { ...base, icon: choice.icon }) as never));
          }}
        />
      )}
    </Sub>
  );
}

function FormLayoutFold({ options, template, apply }: { options: InspectorOptions; template: Template; apply: Apply }) {
  const [refusal, check] = useChecked(options.registry, template, apply);
  const layout = layoutOf(template);
  const current = currentLayout(options.registry, template);
  return (
    <Sub title="Modèle de page">
      <button type="button" className="ay-v2-model" onClick={() => options.onChooseLayout(template, apply)}>
        <Icon name="layout" size={18} />
        <span>
          <strong>{layout === undefined ? 'Une colonne' : fr(current?.body.label, 'Modèle')}</strong>
          <span className="ay-studio__muted">{NAVIGATION_LABELS[layout?.navigation?.mode ?? current?.body.navigation.default ?? 'single']}</span>
        </span>
        <span className="ay-v2-link">Changer</span>
      </button>
      {layout !== undefined && (
        <>
          <Line label="Nature">
            <select aria-label="Nature" value={layout.intent ?? ''} onChange={(event) => check(setLayoutField(template, 'intent', (event.target.value === '' ? undefined : event.target.value) as never))}>
              <option value="">Non précisée</option>
              {INTENTS.map((intent) => <option key={intent.value} value={intent.value}>{intent.label}</option>)}
            </select>
          </Line>
          <Choice label="Densité" value={layout.density ?? 'comfortable'} options={[{ value: 'compact', label: 'Compacte' }, { value: 'comfortable', label: 'Confortable' }, { value: 'gloves', label: 'Gants' }]} onChange={(value) => check(setLayoutField(template, 'density', value as never))} />
        </>
      )}
      <Refusal message={refusal} />
    </Sub>
  );
}

function FormEnd({ template, apply }: { template: Template; apply: Apply }) {
  const layout = layoutOf(template);
  const confirmation = layout?.confirmation;
  const success = layout?.success;
  const sources = valueSources(template);
  const keep = <T extends object>(value: T): T => Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as T;
  return (
    <>
      <Sub title="Confirmation avant validation">
        <Switch label="Demander une confirmation" checked={confirmation !== undefined} onChange={(on) => apply(setLayoutField(template, 'confirmation', on ? { title: { fr: 'Valider la fiche ?' } } : undefined))} />
        {confirmation !== undefined && (
          <>
            <LocalizedText label="Question" required pointer="/layout/confirmation/title" value={confirmation.title as Localized} onChange={(value) => apply(setLayoutField(template, 'confirmation', { ...confirmation, title: (value ?? { fr: '' }) as Localized }), 'confirmation.title')} />
            <TextComposer label="Message" value={confirmation.message} sources={sources} onChange={(value) => apply(setLayoutField(template, 'confirmation', keep({ ...confirmation, message: value }) as never), 'confirmation.message')} />
            <LocalizedText label="Bouton de confirmation" pointer="/layout/confirmation/confirm_label" value={confirmation.confirm_label as Localized | undefined} onChange={(value) => apply(setLayoutField(template, 'confirmation', keep({ ...confirmation, confirm_label: value }) as never), 'confirmation.confirm')} />
            <LocalizedText label="Bouton pour revenir" pointer="/layout/confirmation/cancel_label" value={confirmation.cancel_label as Localized | undefined} onChange={(value) => apply(setLayoutField(template, 'confirmation', keep({ ...confirmation, cancel_label: value }) as never), 'confirmation.cancel')} />
          </>
        )}
      </Sub>
      <Sub title="Écran de succès">
        <Switch label="Montrer un écran de succès" checked={success !== undefined} onChange={(on) => apply(setLayoutField(template, 'success', on ? { title: { fr: 'Fiche enregistrée' }, actions: ['back_to_inbox'] } : undefined))} />
        {success !== undefined && (
          <>
            <LocalizedText label="Titre" pointer="/layout/success/title" value={success.title as Localized | undefined} onChange={(value) => apply(setLayoutField(template, 'success', keep({ ...success, title: value }) as never), 'success.title')} />
            {SUCCESS_ACTIONS.map(([value, label]) => (
              <Switch key={value} label={label} checked={(success.actions ?? []).includes(value as never)} onChange={(on) => {
                const actions = SUCCESS_ACTIONS.map(([candidate]) => candidate).filter((candidate) => (candidate === value ? on : (success.actions ?? []).includes(candidate as never)));
                apply(setLayoutField(template, 'success', keep({ ...success, actions: actions.length === 0 ? undefined : actions }) as never));
              }} />
            ))}
          </>
        )}
      </Sub>
    </>
  );
}

/** Totaux d'affichage : sur une liste de lignes, avec leur unité. */
function Totals({ template, apply }: { template: Template; apply: Apply }) {
  const lists = template.sections.filter((section) => section.series !== undefined || section.repeat !== undefined);
  if (lists.length === 0) return null;
  return (
    <Sub title="Totaux">
      {aggregatesOf(template).map((aggregate, index) => {
        const source = lists.find((section) => section.section_id === aggregate.source);
        const columns = (source?.fields ?? []).map((entry) => ({ value: entry.field.field_id, label: text(entry.field.label, 'fr', 'colonne') }));
        return (
          <fieldset key={aggregate.aggregate_id} className="ay-v2-card">
            <legend className="ay-field__label">{fr(aggregate.label, 'Total')}</legend>
            <LocalizedText label="Nom" required pointer={`/layout/aggregates/${index}/label`} value={aggregate.label as Localized} onChange={(value) => apply(updateAggregate(template, aggregate.aggregate_id, { label: (value ?? { fr: '' }) as Localized }), `aggregate:${aggregate.aggregate_id}:label`)} />
            <Line label="Calcul">
              <select aria-label="Calcul" value={aggregate.op} onChange={(event) => apply(updateAggregate(template, aggregate.aggregate_id, { op: event.target.value as Aggregate['op'], ...(event.target.value === 'count' ? { field: undefined } : { field: aggregate.field ?? columns[0]?.value }) }))}>
                {Object.entries(OPS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </Line>
            <Line label="Liste">
              <select aria-label="Liste" value={aggregate.source} onChange={(event) => apply(updateAggregate(template, aggregate.aggregate_id, { source: event.target.value, field: undefined, group_by: undefined }))}>
                {lists.map((section) => <option key={section.section_id} value={section.section_id}>{text(section.label, 'fr', 'liste')}</option>)}
              </select>
            </Line>
            {aggregate.op !== 'count' && (
              <Line label="Colonne">
                <select aria-label="Colonne" value={aggregate.field ?? ''} onChange={(event) => apply(updateAggregate(template, aggregate.aggregate_id, { field: event.target.value }))}>
                  {columns.map((column) => <option key={column.value} value={column.value}>{column.label}</option>)}
                </select>
              </Line>
            )}
            {['sum', 'min', 'max', 'avg'].includes(aggregate.op) && (
              <Line label="Unité">
                <input type="text" aria-label="Unité" className="ay-v2-short" value={aggregate.unit ?? ''} onChange={(event) => apply(updateAggregate(template, aggregate.aggregate_id, { unit: event.target.value === '' ? undefined : event.target.value }), `aggregate:${aggregate.aggregate_id}:unit`)} />
              </Line>
            )}
            <button type="button" className="ay-v2-link is-danger" onClick={() => apply(removeAggregate(template, aggregate.aggregate_id))}>Retirer</button>
          </fieldset>
        );
      })}
      <AddButton label="Ajouter un total" onClick={() => apply(addAggregate(template, { source: lists[0]!.section_id, op: 'count', label: { fr: `Nombre de ${text(lists[0]!.label, 'fr', 'lignes').toLocaleLowerCase('fr')}` } }).template)} />
    </Sub>
  );
}

function formView(options: InspectorOptions, template: Template, apply: Apply): InspectorView {
  const catalog = options.catalog ?? EMPTY_CATALOG;
  const layout = layoutOf(template);
  const ends = Number(layout?.confirmation !== undefined) + Number(layout?.success !== undefined);
  return {
    title: text(template.label, 'fr', 'Formulaire'),
    subtitle: 'Formulaire',
    icon: 'clipboard',
    tabs: [
      { id: 'content', label: 'Contenu', render: () => (
        <>
          <FormIdentity options={options} template={template} apply={apply} />
          <Totals template={template} apply={apply} />
        </>
      ) },
      { id: 'layout', label: 'Disposition', summary: NAVIGATION_LABELS[layout?.navigation?.mode ?? 'single'] ?? 'Une page', render: () => <FormLayoutFold options={options} template={template} apply={apply} /> },
      { id: 'controls', label: 'Contrôles', open: false, summary: ends === 0 ? 'Aucun' : String(ends), render: () => <FormEnd template={template} apply={apply} /> },
      { id: 'events', label: 'Réactions', open: false, render: () => <FormMoments template={template} apply={apply} catalog={catalog} /> },
    ],
  };
}

// ---- Bandeau du haut.

function HeaderContent({ options, template, apply }: { options: InspectorOptions; template: Template; apply: Apply }) {
  const layout = layoutOf(template);
  const header = (layout?.header ?? {}) as { date?: string; operator?: string; environment_badge?: boolean; show_visual?: boolean; submit_shortcut?: { show?: string; appearance?: string; label?: Localized; icon?: string; next_style?: string } };
  const shortcut = header.submit_shortcut ?? {};
  const conflict = primaryConflict(template);
  return (
    <>
      <FormIdentity options={options} template={template} apply={apply} />
      <Sub title="Bandeau">
        <Switch label="Image du formulaire" checked={header.show_visual !== false} onChange={(on) => apply(setHeader(template, { show_visual: on ? undefined : false }))} />
        <Choice label="Date de la fiche" value={header.date ?? 'datetime'} options={[{ value: 'datetime', label: 'Date et heure' }, { value: 'date', label: 'Date' }, { value: 'none', label: 'Rien' }]} onChange={(value) => apply(setHeader(template, { date: value === 'datetime' ? undefined : value }))} />
        <Choice label="Opérateur" value={header.operator ?? 'photo_and_name'} options={[{ value: 'photo_and_name', label: 'Photo et nom' }, { value: 'name', label: 'Nom' }, { value: 'none', label: 'Rien' }]} onChange={(value) => apply(setHeader(template, { operator: value === 'photo_and_name' ? undefined : value }))} />
        <Switch label="Environnement" help="Puce « Développement » ou « Test » sous le nom de l’opérateur." checked={header.environment_badge === true} onChange={(on) => apply(setHeader(template, { environment_badge: on ? true : undefined }))} />
        <Line label="Bouton Fermer" help="« Sortir » reste toujours au bandeau.">
          <span className="ay-v2-pill">Toujours</span>
        </Line>
      </Sub>
      <Sub title="Raccourci « Valider »">
        <Choice label="Afficher" value={shortcut.show ?? 'always'} options={[{ value: 'always', label: 'Toujours' }, { value: 'before_last_page', label: 'Avant la fin' }, { value: 'never', label: 'Jamais' }]} onChange={(value) => apply(setShortcut(template, { show: value === 'always' ? undefined : value }))} />
        {shortcut.show !== 'never' && (
          <>
            <Line label="Apparence" stacked>
              <span className="ay-v2-appearances" role="radiogroup" aria-label="Apparence du raccourci">
                {[{ value: 'link', label: 'Lien', sample: 'Valider' }, { value: 'secondary', label: 'Secondaire', sample: 'VALIDER' }, { value: 'primary', label: 'Principal', sample: 'VALIDER' }].map((option) => (
                  <button key={option.value} type="button" role="radio" aria-checked={(shortcut.appearance ?? 'link') === option.value} onClick={() => apply(setShortcut(template, { appearance: option.value === 'link' ? undefined : option.value }))}>
                    <span className={`ay-v2-sample is-${option.value}`}>{option.sample}</span>
                    <span>{option.label}</span>
                  </button>
                ))}
              </span>
            </Line>
            <LocalizedText label="Libellé" pointer="/layout/header/submit_shortcut/label" placeholder="Valider maintenant" value={shortcut.label} onChange={(value) => apply(setShortcut(template, { label: value }), 'shortcut.label')} />
            <Line label="Icône">
              <select aria-label="Icône du raccourci" value={shortcut.icon ?? ''} onChange={(event) => apply(setShortcut(template, { icon: event.target.value === '' ? undefined : event.target.value }))}>
                <option value="">—</option>
                {OPTION_ICONS.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </Line>
            {shortcut.appearance === 'primary' && (
              <Choice label="« Suivant »" value={shortcut.next_style ?? 'keep'} options={[{ value: 'demote', label: 'En secondaire' }, { value: 'keep', label: 'Garder' }]} onChange={(value) => apply(setShortcut(template, { next_style: value === 'keep' ? undefined : value }))} />
            )}
          </>
        )}
        {conflict && <p className="ay-v2-warning" role="alert"><Icon name="alert" size={12} /> Deux actions principales</p>}
      </Sub>
    </>
  );
}

function headerView(options: InspectorOptions, template: Template, apply: Apply): InspectorView {
  return {
    title: 'Bandeau du haut',
    subtitle: 'Commun à toutes les pages',
    icon: 'id-card',
    tabs: [{ id: 'content', label: 'Contenu', render: () => <HeaderContent options={options} template={template} apply={apply} /> }],
  };
}

// ---- Barre du bas.

function FooterContent({ template, apply }: { template: Template; apply: Apply }) {
  const footer = (layoutOf(template)?.footer ?? {}) as { start?: string; center?: string; end?: string; hidden?: boolean; background?: string; next_label?: Localized; submit_label?: Localized; next_icon?: string; next_appearance?: string };
  const slots: Array<['start' | 'center' | 'end', string, string]> = [['start', 'Gauche', 'previous'], ['center', 'Centre', 'progress'], ['end', 'Droite', 'next']];
  const blocks = blocksOf(template).map((block) => ({ value: block.block_id, label: `Bloc : ${fr(block.label, 'sans nom')}` }));
  const unreachable = submitUnreachable(template);
  return (
    <>
      <Sub title="Barre">
        <Switch label="Masquer la barre du bas" checked={footer.hidden === true} onChange={(on) => apply(setFooter(template, { hidden: on ? true : undefined }))} />
        {footer.hidden === true && (
          <p className={unreachable ? 'ay-v2-warning' : 'ay-studio__muted'} role={unreachable ? 'alert' : undefined}>
            <Icon name="alert" size={12} /> {unreachable ? 'Aucun bouton Valider' : 'Valider : raccourci du bandeau'}
            {unreachable && <button type="button" className="ay-v2-link" onClick={() => apply(setShortcut(template, { show: undefined, appearance: 'primary' }))}>VALIDER en haut</button>}
          </p>
        )}
        <Choice label="Fond" value={footer.background ?? 'plain'} options={[{ value: 'plain', label: 'Blanc' }, { value: 'tinted', label: 'Teinté' }]} onChange={(value) => apply(setFooter(template, { background: value === 'plain' ? undefined : value }))} />
      </Sub>
      {footer.hidden !== true && (
        <>
          <Sub title="Emplacements">
            {slots.map(([slot, label, fallback]) => (
              <Line key={slot} label={label}>
                <select aria-label={`Emplacement ${label.toLocaleLowerCase('fr')}`} value={footer[slot] ?? fallback} onChange={(event) => apply(setFooter(template, { [slot]: event.target.value === fallback ? undefined : event.target.value }))}>
                  {[...FOOTER, ...blocks].map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              </Line>
            ))}
          </Sub>
          <Sub title="Bouton de droite">
            <LocalizedText label="Libellé" pointer="/layout/footer/next_label" placeholder="Suivant" value={footer.next_label} onChange={(value) => apply(setFooter(template, { next_label: value }), 'footer.next')} />
            <LocalizedText label="Libellé sur la dernière page" pointer="/layout/footer/submit_label" placeholder="Valider la fiche" value={footer.submit_label} onChange={(value) => apply(setFooter(template, { submit_label: value }), 'footer.submit')} />
            <Line label="Icône">
              <select aria-label="Icône du bouton" value={footer.next_icon ?? ''} onChange={(event) => apply(setFooter(template, { next_icon: event.target.value === '' ? undefined : event.target.value }))}>
                <option value="">—</option>
                {OPTION_ICONS.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
            </Line>
            <Choice label="Apparence" value={footer.next_appearance ?? 'primary'} options={[{ value: 'link', label: 'Lien' }, { value: 'secondary', label: 'Secondaire' }, { value: 'primary', label: 'Principal' }]} onChange={(value) => apply(setFooter(template, { next_appearance: value === 'primary' ? undefined : value }))} />
          </Sub>
        </>
      )}
    </>
  );
}

function footerView(template: Template, apply: Apply): InspectorView {
  const hidden = (layoutOf(template)?.footer as { hidden?: boolean } | undefined)?.hidden === true;
  return {
    title: 'Barre du bas',
    subtitle: hidden ? 'Masquée sur l’appareil' : 'Commune à toutes les pages',
    icon: 'columns',
    tabs: [{ id: 'content', label: 'Contenu', render: () => <FooterContent template={template} apply={apply} /> }],
  };
}

// ---- Conteneur de pages.

function PagesContent({ options, template, apply, select }: { options: InspectorOptions; template: Template; apply: Apply; select?: ((id: string) => void) | undefined }) {
  const [renaming, setRenaming] = useState<string | null>(null);
  const [copying, setCopying] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [dragged, setDragged] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const pages = declaredPages(template);
  const effective = formPages(template);
  const layout = layoutOf(template);
  const mode = layout?.navigation?.mode ?? currentLayout(options.registry, template)?.body.navigation.default ?? 'single';
  const allowed = (currentLayout(options.registry, template)?.body.navigation.allowed ?? ['single', 'tabs', 'wizard', 'steps_with_summary']) as string[];
  const describe = (pageId: string): string => {
    const sections = effective.find((page) => page.page_id === pageId)?.sections ?? [];
    const fields = sections.reduce((total, id) => total + (template.sections.find((section) => section.section_id === id)?.fields.length ?? 0), 0);
    const blocks = blocksOf(template).filter((block) => block.page_ids?.includes(pageId)).length;
    return `${fields} champ${fields > 1 ? 's' : ''}${blocks > 0 ? ` · ${blocks} bloc${blocks > 1 ? 's' : ''}` : ''}`;
  };
  const add = (): void => {
    const result = addBlankPage(template, options.registry);
    if ('refused' in result) setRefusal(result.refused);
    else {
      setRefusal(null);
      apply(result.document);
    }
  };
  const copied = copying === null ? undefined : pages.find((page) => page.page_id === copying);
  const removed = deleting === null ? undefined : pages.find((page) => page.page_id === deleting);
  return (
    <>
      <Sub title="Pages">
        {pages.length === 0 ? (
          <Note>Une seule page.</Note>
        ) : (
          <ol className="ay-v2-pages" aria-label="Pages du formulaire">
            {pages.map((page, index) => (
              <li
                key={page.page_id}
                draggable={renaming === null}
                className={dragged === page.page_id ? 'is-dragged' : undefined}
                onDragStart={(event) => {
                  setDragged(page.page_id);
                  event.dataTransfer.effectAllowed = 'move';
                }}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  if (dragged !== null && dragged !== page.page_id) apply(movePageTo(template, dragged, index));
                  setDragged(null);
                }}
                onDragEnd={() => setDragged(null)}
              >
                <span className="ay-v2-pages__grip" aria-hidden="true"><Icon name="grip" size={14} /></span>
                <span className="ay-v2-pages__index">{index + 1}</span>
                {renaming === page.page_id ? (
                  <input
                    autoFocus
                    type="text"
                    aria-label={`Nom de la page ${index + 1}`}
                    defaultValue={fr(page.label, 'Page')}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
                      if (event.key === 'Escape') {
                        event.stopPropagation();
                        setRenaming(null);
                      }
                    }}
                    onBlur={(event) => {
                      const value = event.target.value.trim();
                      setRenaming(null);
                      if (value !== '' && value !== fr(page.label, '')) apply(updatePage(template, page.page_id, { label: { ...(page.label as Localized), fr: value } }));
                    }}
                  />
                ) : (
                  <button type="button" className="ay-v2-pages__name" onClick={() => select?.(`page:${page.page_id}`)} onDoubleClick={() => setRenaming(page.page_id)}>
                    <strong>{fr(page.label, 'Page')}</strong>
                    <span className="ay-studio__muted">{describe(page.page_id)}</span>
                  </button>
                )}
                <span className="ay-v2-pages__tools">
                  <button type="button" className="ay-studio__icon ay-studio__icon--sm" aria-label={`Monter « ${fr(page.label, 'Page')} »`} title="Monter" disabled={index === 0} onClick={() => apply(movePage(template, page.page_id, -1))}><Icon name="arrow-up" size={12} /></button>
                  <button type="button" className="ay-studio__icon ay-studio__icon--sm" aria-label={`Descendre « ${fr(page.label, 'Page')} »`} title="Descendre" disabled={index === pages.length - 1} onClick={() => apply(movePage(template, page.page_id, 1))}><Icon name="arrow-down" size={12} /></button>
                  <button type="button" className="ay-studio__icon ay-studio__icon--sm" aria-label={`Renommer « ${fr(page.label, 'Page')} »`} title="Renommer" onClick={() => setRenaming(page.page_id)}><Icon name="pencil" size={12} /></button>
                  <button type="button" className="ay-studio__icon ay-studio__icon--sm" aria-label={`Dupliquer « ${fr(page.label, 'Page')} »`} title="Dupliquer" onClick={() => setCopying(page.page_id)}><Icon name="copy" size={12} /></button>
                  <button type="button" className="ay-studio__icon ay-studio__icon--sm is-danger" aria-label={`Supprimer « ${fr(page.label, 'Page')} »`} title={pages.length < 2 ? 'Une page au moins' : 'Supprimer'} disabled={pages.length < 2} onClick={() => setDeleting(page.page_id)}><Icon name="trash" size={12} /></button>
                </span>
              </li>
            ))}
          </ol>
        )}
        <AddButton label="Ajouter une page" onClick={add} />
        <Refusal message={refusal} />
      </Sub>
      <Sub title="Navigation entre les pages">
        <Choice label="Mode" value={mode} options={(['single', 'tabs', 'wizard', 'steps_with_summary'] as const).filter((value) => allowed.includes(value)).map((value) => ({ value, label: NAVIGATION_LABELS[value]! }))} onChange={(value) => {
          const next = setNavigation(template, { mode: value as never });
          const refused = refusalOf(options.registry, template, next);
          setRefusal(refused);
          if (refused === null) apply(next);
        }} />
        <Choice label="Sur téléphone" value={layout?.navigation?.mobile ?? 'keep'} options={[{ value: 'keep', label: 'Pareil' }, { value: 'wizard', label: 'Étapes' }, { value: 'single', label: 'Une page' }]} onChange={(value) => apply(setNavigation(template, { mobile: value === 'keep' ? undefined : (value as never) }))} help="Sur téléphone, les onglets peuvent devenir des étapes (« 2/3 Pièces ▾ »)." />
      </Sub>
      {copied !== undefined && (
        <NameDialog
          title={`Dupliquer la page « ${fr(copied.label, 'Page')} »`}
          subtitle={`Page ${pages.indexOf(copied) + 1} sur ${pages.length}`}
          proposal={copyName(fr(copied.label, 'Page'), pages.map((page) => fr(page.label, '')))}
          onClose={() => setCopying(null)}
          onConfirm={(name) => {
            setCopying(null);
            const result = duplicatePage(template, copied.page_id, name);
            apply(result.template);
            select?.(`page:${result.id}`);
          }}
        />
      )}
      {removed !== undefined && (
        <DeletePageDialog
          name={fr(removed.label, 'Page')}
          position={`Page ${pages.indexOf(removed) + 1} sur ${pages.length}`}
          fields={pageFieldLabels(template, removed.page_id)}
          onClose={() => setDeleting(null)}
          onConfirm={(withFields) => {
            setDeleting(null);
            apply(deletePageV2(template, removed.page_id, withFields));
          }}
        />
      )}
    </>
  );
}

function pagesView(options: InspectorOptions, template: Template, apply: Apply, select?: (id: string) => void): InspectorView {
  const mode = layoutOf(template)?.navigation?.mode ?? 'single';
  return {
    title: 'Pages du formulaire',
    subtitle: `Conteneur de pages · ${NAVIGATION_LABELS[mode] ?? 'Une page'}`,
    icon: 'layers',
    tabs: [{ id: 'content', label: 'Contenu', render: () => <PagesContent options={options} template={template} apply={apply} select={select} /> }],
  };
}

// ---- Page.

function pageView(options: InspectorOptions, template: Template, pageId: string, apply: Apply): InspectorView {
  const page = declaredPages(template).find((candidate) => candidate.page_id === pageId);
  if (page === undefined) return { title: 'Page', subtitle: 'Page', tabs: [] };
  const index = declaredPages(template).indexOf(page);
  const effective = formPages(template).find((candidate) => candidate.page_id === pageId);
  const catalog = options.catalog ?? EMPTY_CATALOG;
  return {
    title: fr(page.label, 'Page'),
    subtitle: `Page ${index + 1} sur ${declaredPages(template).length}`,
    icon: 'file',
    tabs: [
      { id: 'content', label: 'Contenu', render: () => (
        <>
          <Sub>
            <LocalizedText label="Titre" required pointer={`/layout/pages/${index}/label`} value={page.label as Localized} onChange={(value) => apply(updatePage(template, pageId, { label: (value ?? { fr: '' }) as Localized }), `page:${pageId}:label`)} />
          </Sub>
          <Sub title="Parties de cette page">
            {template.sections.map((section) => (
              <Switch key={section.section_id} label={text(section.label, 'fr', 'Partie')} checked={(effective?.sections ?? []).includes(section.section_id)} onChange={(on) => on && apply(moveSectionToPage(template, section.section_id, pageId))} />
            ))}
          </Sub>
          <Sub title="Compteur">
            <Line label="Compteur de la page">
              <select aria-label="Compteur de la page" value={page.counter?.aggregate_ref ?? ''} onChange={(event) => apply(updatePage(template, pageId, { counter: event.target.value === '' ? undefined : { aggregate_ref: event.target.value } }))}>
                <option value="">Aucun</option>
                {aggregatesOf(template).map((aggregate) => <option key={aggregate.aggregate_id} value={aggregate.aggregate_id}>{fr(aggregate.label, 'Total')}</option>)}
              </select>
            </Line>
          </Sub>
          <DeveloperFold options={options}><InternalName value={pageId} onRename={(next) => apply(renameId(template, 'page', pageId, next))} /></DeveloperFold>
        </>
      ) },
      { id: 'controls', label: 'Contrôles', open: false, summary: visibleWord(page.visible_when), render: () => (
        <>
          <Rule label="Visible" mode={page.visible_when === undefined ? 'always' : page.visible_when === false ? 'never' : 'when'} condition={page.visible_when} variables={answerScope(template)} onChange={(mode, condition) => apply(updatePage(template, pageId, { visible_when: (mode === 'always' ? undefined : mode === 'never' ? false : condition) as never }))} help="Une page cachée n’exige aucune réponse ; le serveur revérifie tout." />
          <Switch label="« Suivant » ne vérifie pas cette page" checked={page.allow_skip === true} onChange={(on) => apply(updatePage(template, pageId, { allow_skip: on ? true : undefined }))} />
        </>
      ) },
      { id: 'events', label: 'Réactions', open: false, render: () => <ReactionEditor template={template} apply={apply} catalog={catalog} trigger={{ on: 'page_validate', page_id: pageId }} heading={<span className="ay-v2-sentence"><span className="ay-v2-word">Quand</span><strong className="ay-v2-token">{fr(page.label, 'la page')}</strong><span className="ay-v2-token is-muted">est validée</span></span>} /> },
    ],
  };
}

// ---- Partie.

/** Place d'une partie (zone, carte, colonnes, lignes) ou son apparence (mise en avant, taille). */
function SectionPlace({ options, template, sectionId, apply, part }: { options: InspectorOptions; template: Template; sectionId: string; apply: Apply; part: 'layout' | 'appearance' }) {
  const [refusal, check] = useChecked(options.registry, template, apply);
  const body = currentLayout(options.registry, template)?.body;
  const section = template.sections.find((candidate) => candidate.section_id === sectionId)!;
  const placement = layoutOf(template)?.placements?.[sectionId] ?? {};
  if (layoutOf(template) === undefined || body === undefined) return null;
  const set = (patch: Parameters<typeof setPlacement>[2]): void => check(setPlacement(template, sectionId, patch));
  return (
    <Sub>
      {part === 'appearance' ? (
        <>
          <Choice label="Mise en avant" value={placement.emphasis ?? 'none'} options={EMPHASIS} onChange={(value) => set({ emphasis: value === 'none' ? undefined : value })} />
          <Choice label="Taille" value={placement.size ?? 'normal'} options={Object.entries(SIZES).map(([value, label]) => ({ value, label }))} onChange={(value) => set({ size: value === 'normal' ? undefined : (value as never) })} />
        </>
      ) : (
        <>
      <Line label="Zone">
        <select aria-label="Zone" value={sectionRegion(body, placement.region) ?? ''} onChange={(event) => set({ region: event.target.value })}>
          {body.regions.filter((region) => region.accepts.includes('section')).map((region) => <option key={region.region_id} value={region.region_id}>{fr(region.label)}</option>)}
        </select>
      </Line>
      <Switch label="Dans une carte" checked={placement.card === true} onChange={(on) => set({ card: on ? true : undefined })} />
      <Choice label="Repliable" value={placement.collapsible ?? 'none'} options={[{ value: 'none', label: 'Non' }, { value: 'open', label: 'Ouverte' }, { value: 'closed', label: 'Fermée' }]} onChange={(value) => set({ collapsible: value === 'none' ? undefined : value })} />
      <Choice label="Colonnes" value={placement.columns?.landscape ?? 1} options={[1, 2, 3].map((value) => ({ value, label: String(value) }))} onChange={(value) => set({ columns: { ...(placement.columns ?? {}), landscape: value } })} help="En paysage ; sur téléphone, une colonne." />
      {(section.series !== undefined || section.repeat !== undefined) && (
        <Choice label="Lignes" value={placement.presentation?.as ?? 'form'} options={[{ value: 'form', label: 'Formulaire' }, { value: 'table', label: 'Tableau' }, { value: 'cards', label: 'Cartes' }, { value: 'list_detail', label: 'Liste et détail' }]} onChange={(value) => set({ presentation: value === 'form' ? undefined : { as: value as never }, ...(value !== 'table' && value !== 'list_detail' ? { entry_panel: undefined } : {}) })} />
      )}
      {section.series !== undefined && (placement.presentation?.as === 'table' || (placement.presentation?.as as string | undefined) === 'list_detail') && (
        <Line label="Panneau de saisie">
          <select aria-label="Panneau de saisie" value={placement.entry_panel?.region ?? ''} onChange={(event) => set({ entry_panel: event.target.value === '' ? undefined : { region: event.target.value, fixed: true } })}>
            <option value="">Sans panneau</option>
            {body.regions.filter((region) => region.role !== 'header' && region.role !== 'footer').map((region) => <option key={region.region_id} value={region.region_id}>{fr(region.label)}</option>)}
          </select>
        </Line>
      )}
      {section.series !== undefined && (placement.presentation?.as === 'table' || (placement.presentation?.as as string | undefined) === 'list_detail') && (
        // 1.12.0 (ADR-146 addendum (21)) : libellé court de l'en-tête de chaque colonne (« POIDS » pour « Poids brut »).
        <div className="ay-v2-columns" role="group" aria-label="En-têtes des colonnes">
          <span className="ay-v2-columns__title">En-têtes des colonnes</span>
          {section.fields
            .filter((entry) => entry.compute === undefined)
            .map((entry) => {
              const id = entry.field.field_id;
              const labels = (placement as { column_labels?: Record<string, Localized> }).column_labels ?? {};
              return (
                <LocalizedText
                  key={id}
                  label={text(entry.field.label, 'fr', 'Colonne')}
                  placeholder={text(entry.field.label, 'fr', '')}
                  help="Laissé vide, la colonne reprend le libellé du champ."
                  pointer={`/layout/placements/${sectionId}/column_labels/${id}`}
                  value={labels[id]}
                  onChange={(value) => {
                    const next = { ...labels };
                    if (value === undefined) delete next[id];
                    else next[id] = value;
                    set({ column_labels: Object.keys(next).length === 0 ? undefined : next } as never);
                  }}
                />
              );
            })}
        </div>
      )}
        </>
      )}
      <Refusal message={refusal} />
    </Sub>
  );
}

function sectionView(options: InspectorOptions, template: Template, sectionId: string, apply: Apply): InspectorView {
  const s = template.sections.findIndex((section) => section.section_id === sectionId);
  const section = template.sections[s];
  if (section === undefined) return { title: 'Partie', subtitle: 'Partie', tabs: [] };
  const zone = sectionRegion(currentLayout(options.registry, template)?.body, layoutOf(template)?.placements?.[sectionId]?.region);
  const zoneLabel = fr(currentLayout(options.registry, template)?.body.regions.find((region) => region.region_id === zone)?.label, '');
  const group = templateProperties(options.schemas, options.rules, template, `/sections/${s}`);
  const catalog = options.catalog ?? EMPTY_CATALOG;
  const placement = layoutOf(template)?.placements?.[sectionId];
  const placed = layoutOf(template) !== undefined && currentLayout(options.registry, template) !== undefined;
  return {
    title: text(section.label, 'fr', 'Partie'),
    subtitle: `${section.series !== undefined ? 'Liste de lignes' : section.repeat !== undefined ? 'Partie répétée' : 'Partie'}${zoneLabel === '' ? '' : ` · ${zoneLabel}`}`,
    icon: section.series !== undefined || section.repeat !== undefined ? 'rows' : 'layers',
    tabs: [
      { id: 'content', label: 'Contenu', render: () => (
        <>
          <Sub>
            <LocalizedText label="Titre" required pointer={`/sections/${s}/label`} value={section.label as Localized} onChange={(value) => apply(setAt(template, `/sections/${s}/label`, value ?? { fr: '' }), `section:${sectionId}:label`)} />
          </Sub>
          {section.series !== undefined && group !== null && (
            <Sub title="Lignes">
              <PropertyPanel group={{ ...pickProperties(group, { series: ['min_count', 'max_count'] }), title: '' }} document={template} onChange={(pointer, value, coalesce) => apply(setAt(template, pointer, value), coalesce === undefined ? undefined : pointer)} />
            </Sub>
          )}
          <DeveloperFold options={options}><InternalName value={sectionId} /></DeveloperFold>
        </>
      ) },
      ...(placed
        ? [
            { id: 'layout', label: 'Disposition', summary: placement?.card === true ? 'Carte' : `${placement?.columns?.landscape ?? 1} col.`, render: () => <SectionPlace options={options} template={template} sectionId={sectionId} apply={apply} part="layout" /> },
            { id: 'appearance', label: 'Apparence', open: false, summary: SIZES[placement?.size ?? 'normal'] ?? 'Normale', render: () => <SectionPlace options={options} template={template} sectionId={sectionId} apply={apply} part="appearance" /> },
          ]
        : []),
      { id: 'controls', label: 'Contrôles', open: false, summary: visibleWord(section.visible_when), render: () => (
        <>
          <Rule label="Visible" mode={section.visible_when === undefined ? 'always' : section.visible_when === false ? 'never' : 'when'} condition={section.visible_when} variables={answerScope(template)} onChange={(mode, condition) => apply(setAt(template, `/sections/${s}/visible_when`, mode === 'always' ? undefined : mode === 'never' ? false : condition))} />
          {layoutOf(template) !== undefined && (
            <Rule label="Lecture seule" order={['never', 'when', 'always']} mode={placement?.readonly_when === undefined ? 'never' : placement.readonly_when === true ? 'always' : 'when'} condition={placement?.readonly_when} variables={answerScope(template)} onChange={(mode, condition) => apply(setPlacement(template, sectionId, { readonly_when: (mode === 'never' ? undefined : mode === 'always' ? true : condition) as never }))} />
          )}
        </>
      ) },
      ...(section.series === undefined ? [] : [{ id: 'events', label: 'Réactions', open: false, render: () => <ReactionEditor template={template} apply={apply} catalog={catalog} trigger={{ on: 'row_add', section_id: sectionId }} heading={<span className="ay-v2-sentence"><span className="ay-v2-word">Quand</span><span className="ay-v2-token is-muted">une ligne est ajoutée à</span><strong className="ay-v2-token">{text(section.label, 'fr', 'la liste')}</strong></span>} /> }]),
    ],
  };
}

// ---- Bloc.

/** Place d'un bloc (zone, pages) ou son apparence (variante, mise en avant, taille). */
function BlockPlace({ options, template, blockId, apply, part }: { options: InspectorOptions; template: Template; blockId: string; apply: Apply; part: 'layout' | 'appearance' }) {
  const [refusal, check] = useChecked(options.registry, template, apply);
  const block = blocksOf(template).find((candidate) => candidate.block_id === blockId)!;
  const declared = blockOf(componentByName(options.registry, blockComponent(block).name));
  const body = currentLayout(options.registry, template)?.body;
  const pages = declaredPages(template);
  return (
    <Sub>
      {part === 'appearance' ? (
        <>
          {(declared?.variants ?? []).length > 0 && <Choice label="Variante" value={block.variant ?? declared!.variants![0]!} options={(declared?.variants ?? []).map((value) => ({ value, label: VARIANTS[value] ?? value }))} onChange={(value) => check(updateBlock(template, blockId, { variant: value }))} />}
          <Choice label="Mise en avant" value={block.emphasis ?? 'none'} options={EMPHASIS} onChange={(value) => check(updateBlock(template, blockId, { emphasis: value === 'none' ? undefined : value }))} />
          {(declared?.sizes ?? []).length > 1 && <Choice label="Taille" value={block.size ?? 'normal'} options={(declared?.sizes ?? []).map((value) => ({ value, label: SIZES[value] ?? value }))} onChange={(value) => check(updateBlock(template, blockId, { size: value === 'normal' ? undefined : (value as never) }))} />}
        </>
      ) : (
        <>
          <Line label="Zone">
            <select aria-label="Zone" value={block.region} onChange={(event) => check(updateBlock(template, blockId, { region: event.target.value }))}>
              {(body?.regions ?? []).filter((region) => region.role !== 'footer').map((region) => <option key={region.region_id} value={region.region_id}>{fr(region.label)}</option>)}
            </select>
          </Line>
          {pages.length > 0 && (
            <>
              <Switch label="Sur toutes les pages" checked={block.page_ids === undefined} onChange={(on) => check(updateBlock(template, blockId, { page_ids: on ? undefined : [pages[0]!.page_id] }))} />
              {block.page_ids !== undefined && pages.map((page) => (
                <Switch key={page.page_id} label={fr(page.label, 'Page')} checked={block.page_ids!.includes(page.page_id)} onChange={(on) => {
                  const kept = pages.map((candidate) => candidate.page_id).filter((id) => (id === page.page_id ? on : block.page_ids!.includes(id)));
                  check(updateBlock(template, blockId, { page_ids: kept.length === 0 ? undefined : (kept as [string, ...string[]]) }));
                }} />
              ))}
            </>
          )}
        </>
      )}
      <Refusal message={refusal} />
    </Sub>
  );
}

function blockView(options: InspectorOptions, template: Template, blockId: string, apply: Apply): InspectorView {
  const block = blocksOf(template).find((candidate) => candidate.block_id === blockId);
  if (block === undefined) return { title: 'Bloc', subtitle: 'Bloc', tabs: [] };
  const component = componentByName(options.registry, blockComponent(block).name);
  const declared = blockOf(component);
  const zone = fr(currentLayout(options.registry, template)?.body.regions.find((region) => region.region_id === block.region)?.label, '');
  const componentLabel = fr(component?.manifest.component.label, 'Bloc');
  const catalog = options.catalog ?? EMPTY_CATALOG;
  const bindingOptions = (expects: string): Array<{ value: string; label: string }> => {
    if (expects === 'aggregates') return [{ value: '$aggregates', label: 'Tous les totaux' }];
    if (expects === 'warnings') return [{ value: '$form.warnings', label: 'Avertissements de la fiche' }];
    const answers = template.sections.filter((section) => section.repeat === undefined && section.series === undefined).flatMap((section) => section.fields).filter((entry) => expects !== 'reference' || entry.field.type === 'reference');
    return [...answers.map((entry) => ({ value: `$answers.${entry.field.field_id}`, label: `Réponse : ${text(entry.field.label, 'fr', 'champ')}` })), ...(expects === 'reference' ? [] : valueSources(template, { aggregates: true, context: true }).map((source) => ({ value: source.ref, label: source.label })))];
  };
  const index = blocksOf(template).indexOf(block);
  // Blocs d'affichage et d'action (ADR-146 addendum (20)) : leurs réglages en phrases, jamais des identifiants.
  const displayBlock = DISPLAY_BLOCKS.has(blockComponent(block).name);
  const texts = (declared?.exposed_props ?? []).filter((prop) => prop.type === 'localized_text');
  const settings = (declared?.exposed_props ?? []).filter((prop) => prop.type !== 'localized_text' && !(displayBlock && DISPLAY_BLOCK_PROPS.has(prop.name)));
  const bindings = displayBlock ? [] : (declared?.bindings ?? []);
  return {
    title: fr(block.label, componentLabel),
    subtitle: `${componentLabel}${zone === '' ? '' : ` · ${zone}`}`,
    ...(component === undefined ? { origin: 'Introuvable' } : component.origin === 'core' ? {} : { origin: layerLabel(catalog, component.origin) }),
    icon: 'layout',
    tabs: [
      { id: 'content', label: 'Contenu', render: () => (
        <>
          <Sub>
            <LocalizedText label="Nom du bloc" pointer={`/layout/blocks/${index}/label`} value={block.label as Localized | undefined} onChange={(value) => apply(updateBlock(template, blockId, { label: value as never }), `block:${blockId}:label`)} />
            {texts.map((prop) => (
              <LocalizedText key={prop.name} label={fr(prop.label, 'Texte')} pointer={`/layout/blocks/${index}/props/${prop.name}`} value={block.props?.[prop.name] as Localized | undefined} onChange={(value) => apply(setBlockProp(template, blockId, prop.name, value), `block:${blockId}:${prop.name}`)} />
            ))}
          </Sub>
          {displayBlock && <DisplayBlockSettings name={blockComponent(block).name} template={template} blockId={blockId} apply={apply} client={options.client} />}
          {settings.length > 0 && (
            <Sub title="Réglages">
              {settings.map((prop) =>
                prop.type === 'boolean' ? (
                  <Switch key={prop.name} label={fr(prop.label, 'Réglage')} checked={block.props?.[prop.name] === true} onChange={(on) => apply(setBlockProp(template, blockId, prop.name, on ? true : undefined))} />
                ) : (
                  <Line key={prop.name} label={fr(prop.label, 'Réglage')}>
                    <input type={prop.type === 'number' ? 'number' : 'text'} aria-label={fr(prop.label, 'Réglage')} value={block.props?.[prop.name] === undefined ? '' : String(block.props[prop.name])} onChange={(event) => apply(setBlockProp(template, blockId, prop.name, event.target.value === '' ? undefined : prop.type === 'number' ? Number(event.target.value) : event.target.value), `block:${blockId}:${prop.name}`)} />
                  </Line>
                ),
              )}
            </Sub>
          )}
          {bindings.length > 0 && (
            <Sub title="Données">
              {bindings.map((binding) => (
                <Line key={binding.name} label={fr(binding.label, 'Donnée')}>
                  <select aria-label={fr(binding.label, 'Donnée')} value={block.bind?.[binding.name]?.path ?? ''} onChange={(event) => apply(setBlockBinding(template, blockId, binding.name, event.target.value === '' ? undefined : event.target.value))}>
                    <option value="">Non reliée</option>
                    {bindingOptions(binding.expects).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </Line>
              ))}
            </Sub>
          )}
          <DeveloperFold options={options}>
            <InternalName value={blockId} onRename={(next) => apply(renameId(template, 'block', blockId, next))} />
            {component !== undefined && <Line label="Rendu"><span className="ay-v2-token">{flutterSupported(component) ? 'Web et tablette' : 'Web seulement'}</span></Line>}
          </DeveloperFold>
        </>
      ) },
      { id: 'layout', label: 'Disposition', ...(zone === '' ? {} : { summary: zone }), render: () => <BlockPlace options={options} template={template} blockId={blockId} apply={apply} part="layout" /> },
      { id: 'appearance', label: 'Apparence', open: false, render: () => <BlockPlace options={options} template={template} blockId={blockId} apply={apply} part="appearance" /> },
      { id: 'controls', label: 'Contrôles', open: false, summary: visibleWord(block.visible_when), render: () => (
        <>
          <Rule label="Visible" mode={block.visible_when === undefined ? 'always' : block.visible_when === false ? 'never' : 'when'} condition={block.visible_when} variables={answerScope(template)} onChange={(mode, condition) => apply(updateBlock(template, blockId, { visible_when: (mode === 'always' ? undefined : mode === 'never' ? false : condition) as never }))} />
          <Rule label="Lecture seule" order={['never', 'when', 'always']} mode={block.readonly_when === undefined ? 'never' : block.readonly_when === true ? 'always' : 'when'} condition={block.readonly_when} variables={answerScope(template)} onChange={(mode, condition) => apply(updateBlock(template, blockId, { readonly_when: (mode === 'never' ? undefined : mode === 'always' ? true : condition) as never }))} />
        </>
      ) },
    ],
  };
}

/** Contexte des langues de l'inspecteur : langue par défaut, langues du formulaire, marques « suggérée ». */
function localeEnv(options: InspectorOptions, template: Template, apply: Apply, pending: Map<string, PendingSuggestion>): LocaleEnv {
  const defaultLocale = options.defaultLocale ?? 'fr';
  const translate = options.translate;
  return {
    defaultLocale,
    locales: templateLocales(template, defaultLocale, options.addedLocales ?? []),
    addLocale: (locale) => options.onAddLocale?.(locale),
    suggestions: template.translation_suggestions ?? [],
    accept: (pointer, locale) => apply(clearSuggestions(template, pointer, locale)),
    ...(translate === undefined
      ? {}
      : {
          suggest: async (source, sourceLocale, locales, pointer) => {
            const result = await translate.suggest([{ pointer: pointer ?? '/text', text: source }], sourceLocale, locales);
            if (result.suggestions.length === 0) throw new Error('Aucune suggestion pour ces langues.');
            // La marque attend l'enregistrement de la fenêtre : elle suit la valeur si l'auteur la garde.
            if (pointer !== undefined) for (const entry of result.suggestions) pending.set(`${entry.mark.pointer}|${entry.mark.locale}`, entry);
            return Object.fromEntries(result.suggestions.map((entry) => [entry.mark.locale, entry.text]));
          },
        }),
  };
}

/** Portée « Cet élément » de la fenêtre « Langues » : préfixes des textes de l'élément choisi, et exclusions. */
function elementScope(template: Template, id: string): [readonly string[], readonly string[]] {
  if (id.startsWith('field:')) return [[id.slice(6)], []];
  if (id.startsWith('section:')) {
    const index = template.sections.findIndex((section) => section.section_id === id.slice(8));
    // La partie seule : ses champs ont chacun leur fenêtre.
    return index < 0 ? [[], []] : [[`/sections/${index}`, `/layout/placements/${id.slice(8)}`], [`/sections/${index}/fields`, `/sections/${index}/repeat`]];
  }
  if (id.startsWith('block:')) {
    const index = blocksOf(template).findIndex((block) => block.block_id === id.slice(6));
    return index < 0 ? [[], []] : [[`/layout/blocks/${index}`], []];
  }
  if (id.startsWith('page:')) {
    const index = declaredPages(template).findIndex((page) => page.page_id === id.slice(5));
    return index < 0 ? [[], []] : [[`/layout/pages/${index}`], []];
  }
  if (id === 'pages') return [['/layout/pages'], []];
  if (id === 'header') return [['/label', '/description', '/layout/header'], []];
  if (id === 'footer') return [['/layout/footer'], []];
  return [['/label', '/description', '/layout/header/overline', '/layout/confirmation', '/layout/success', '/layout/aggregates'], []];
}

/** Inspecteur v2 : la vue de l'élément choisi, ses groupes dans le contexte des langues du formulaire. */
export function formInspector(options: InspectorOptions): InspectorFactory {
  const catalog = options.catalog ?? EMPTY_CATALOG;
  const env = { catalog, presets: options.presets ?? NO_PRESETS, client: options.client, developer: options.developer };
  // Suggestions rendues et pas encore enregistrées : leur marque rejoint le gabarit avec la valeur gardée.
  const pending = new Map<string, PendingSuggestion>();
  return (template, id, applyNext, select) => {
    const apply: Apply = (next, coalesce) => applyNext(keepSuggestionMarks(next, pending), coalesce);
    const view = id.startsWith('page:')
      ? pageView(options, template, id.slice(5), apply)
      : id === 'pages'
        ? pagesView(options, template, apply, select)
        : id === 'header'
          ? headerView(options, template, apply)
          : id === 'footer'
            ? footerView(template, apply)
            : id.startsWith('section:')
              ? sectionView(options, template, id.slice(8), apply)
              : id.startsWith('field:')
                ? fieldViewV2(env, template, id.slice(6), apply)
                : id.startsWith('block:')
                  ? blockView(options, template, id.slice(6), apply)
                  : formView(options, template, apply);
    const locales = localeEnv(options, template, apply, pending);
    // Un seul bouton « Langues » dans l'en-tête : tous les textes de l'élément, ou du formulaire.
    const scope: LanguesScope = { name: view.title, element: scopeTexts(template, ...elementScope(template, id)), form: scopeTexts(template) };
    const port: LanguesPort = {
      ...(options.translate === undefined
        ? {}
        : {
            suggest: async (texts, sourceLocale, targets) => {
              const result = await options.translate!.suggest(texts, sourceLocale, targets);
              // La marque « suggérée » attend l'enregistrement : elle suit la valeur si l'auteur la garde.
              for (const entry of result.suggestions) pending.set(`${entry.mark.pointer}|${entry.mark.locale}`, entry);
              return result.suggestions.map((entry) => ({ pointer: entry.mark.pointer, locale: entry.mark.locale, text: entry.text }));
            },
          }),
      save: (changes, accepted) => {
        let next = template;
        for (const change of changes) next = setAt(next, change.pointer, change.text);
        for (const mark of accepted) {
          pending.delete(`${mark.pointer}|${mark.locale}`);
          next = clearSuggestions(next, mark.pointer, mark.locale);
        }
        apply(next);
      },
    };
    return {
      ...view,
      action: <LocaleProvider env={locales}><LanguesButton scope={scope} port={port} /></LocaleProvider>,
      tabs: view.tabs.map((tab) => ({ ...tab, render: () => <LocaleProvider env={locales}>{tab.render()}</LocaleProvider> })),
    };
  };
}
